import React, { useState, useRef, useEffect } from "react";
import { SelectProps, SelectOption } from "../../../types";
import "./Select.css";

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  className = "",
  disabled = false,
  style,
  name,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const normalizedOptions: SelectOption[] = options.map((opt) => {
    if (typeof opt === "object" && opt !== null) {
      return opt as SelectOption;
    }
    return { label: String(opt), value: opt };
  });

  const selectedOption = normalizedOptions.find(
    (opt) => String(opt.value).toLowerCase() === String(value).toLowerCase()
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleSelectOption = (optValue: string | number) => {
    setIsOpen(false);
    if (onChange) {
      const syntheticEvent = {
        target: {
          value: optValue,
          name: name || "",
          id: id || "",
        },
      } as unknown as React.ChangeEvent<HTMLSelectElement>;
      onChange(syntheticEvent);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }
  };

  const displayLabel = selectedOption
    ? selectedOption.label
    : placeholder || (normalizedOptions[0] ? normalizedOptions[0].label : "");

  const isPlaceholderActive = !selectedOption && !!placeholder;

  return (
    <div
      ref={containerRef}
      className={`custom-select-container ${isOpen ? "is-open" : ""} ${className}`}
      style={style}
    >
      <div
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-disabled={disabled}
        className={`custom-select-trigger ${disabled ? "disabled" : ""}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <span
          className={`custom-select-value ${
            isPlaceholderActive ? "is-placeholder" : ""
          }`}
        >
          {displayLabel}
        </span>
        <svg
          className="custom-select-arrow"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {isOpen && (
        <ul className="custom-select-menu" role="listbox">
          {placeholder && (
            <li
              role="option"
              aria-selected={value === ""}
              className={`custom-select-option ${
                value === "" ? "is-selected" : ""
              }`}
              onClick={() => handleSelectOption("")}
            >
              {placeholder}
            </li>
          )}
          {normalizedOptions.map((opt) => {
            const isSelected =
              String(opt.value).toLowerCase() === String(value).toLowerCase();
            return (
              <li
                key={String(opt.value)}
                role="option"
                aria-selected={isSelected}
                className={`custom-select-option ${
                  isSelected ? "is-selected" : ""
                }`}
                onClick={() => handleSelectOption(opt.value)}
              >
                <span>{opt.label}</span>
                {isSelected && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Select;
